import { useSearchParams } from '@remix-run/react';
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { reducedMotion } from '~/deck2/case/motion';
import { Mark } from '~/deck2/ui/Mark';
import { BringScene } from './scenes/BringScene';
import { PickScene } from './scenes/PickScene';
import { ShapeScene } from './scenes/ShapeScene';
import { ShareScene } from './scenes/ShareScene';
import { FAQ_ID, HOW_IT_WORKS_ID, faq, footer, hero, howItWorks, nav, privacy, useCases } from './config';
import { HeroReel } from './HeroReel';
import { CursorArrow, SceneLoop } from './scenes/Stage';
import { StartFlow } from './StartFlow';
import styles from './landing.module.css';

/** The case study landing: nav, hero with the case study reel, how it works, where it's used, privacy, FAQs, footer, and the start flow. */
export function Landing() {
  // `?start` opens the start flow straight away, for links that promise it, like a case study's last frame.
  const [params] = useSearchParams();
  const [starting, setStarting] = useState(() => params.has('start'));
  const start = useCallback(() => setStarting(true), []);
  const stop = useCallback(() => setStarting(false), []);

  return (
    <div className={styles.page}>
      <Nav />
      <main>
        <Hero onStart={start} />
        <HowItWorks />
        <UseCases />
        <Privacy />
        <Faq />
      </main>
      <Footer />
      {starting ? <StartFlow onClose={stop} /> : null}
    </div>
  );
}

function Nav() {
  return (
    <header className={styles.nav}>
      <div className={styles.navSide}>
        <a className={styles.logo} href="#top" aria-label="Deck" onClick={toSection}>
          <Mark size={26} />
        </a>
      </div>
      <nav className={styles.links} aria-label="Sections">
        {nav.links.map((link) => (
          <a key={link.href} className={styles.link} href={link.href} onClick={toSection}>
            {link.label}
          </a>
        ))}
      </nav>
      <div className={`${styles.navSide} ${styles.navEnd}`}>
        <a className={`${styles.button} ${styles.white} ${styles.small}`} href={nav.signIn.href}>
          {nav.signIn.label}
        </a>
      </div>
    </header>
  );
}

/**
 * A link to a section of this page scrolls there every time. Left to the browser, a second click does nothing
 * once the address already ends in that section's name. A click meant for a new tab is left alone.
 */
function toSection(event: MouseEvent<HTMLAnchorElement>) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const hash = event.currentTarget.getAttribute('href') ?? '';
  const section = hash.startsWith('#') ? document.getElementById(hash.slice(1)) : null;
  if (!section) return;
  event.preventDefault();
  const behavior = reducedMotion() ? 'auto' : 'smooth';
  // The logo goes back to the very top, where the hero sits under the nav rather than behind it.
  if (section.id === 'top') window.scrollTo({ top: 0, behavior });
  else section.scrollIntoView({ behavior, block: 'start' });
  window.history.replaceState(window.history.state, '', hash);
}

const delay = (ms: number) => ({ '--delay': `${ms}ms` }) as CSSProperties;

function Hero({ onStart }: { onStart: () => void }) {
  return (
    <section id="top" className={styles.hero}>
      <h1 className={`${styles.headline} ${styles.rise}`}>
        {hero.headline.map((line) => (
          <span key={line} className={styles.headlineLine}>
            {line}
          </span>
        ))}
      </h1>
      <div className={`${styles.actions} ${styles.rise}`} style={delay(120)}>
        <button type="button" className={`${styles.button} ${styles.ink} ${styles.large}`} onClick={onStart}>
          {hero.primary}
          <Arrow />
        </button>
      </div>
      <HeroReel className={styles.rise} style={delay(240)} />
    </section>
  );
}

function HowItWorks() {
  return (
    <section id={HOW_IT_WORKS_ID} className={styles.how}>
      {howItWorks.steps.map((step) => (
        <article key={step.title} className={styles.step}>
          <div className={styles.stepText}>
            <h3 className={styles.stepTitle}>{step.title}</h3>
            <p className={styles.stepBody}>{step.body}</p>
          </div>
          <div className={styles.panel}>
            <Scene id={step.scene} label={step.label} />
          </div>
        </article>
      ))}
    </section>
  );
}

const SCENES = { pick: PickScene, bring: BringScene, shape: ShapeScene, share: ShareScene };

/** A scene, mounted anew each time it plays again, so every play starts fresh. */
function Scene({ id, label }: { id: keyof typeof SCENES; label: string }) {
  const Drawn = SCENES[id];
  const [round, setRound] = useState(0);
  const again = useCallback(() => setRound((n) => n + 1), []);
  return (
    <SceneLoop.Provider value={again}>
      <Drawn key={round} label={label} />
    </SceneLoop.Provider>
  );
}

/** The dashed lines from the cover down to each place, left to right, in the order they draw. */
const USE_LINES = ['M600 0C600 60 192 36 192 96', 'M600 0V96', 'M600 0C600 60 1008 36 1008 96'];

/**
 * When the cover and each place come in: the cover, then for each place its line draws and its card rises.
 * Only on the wide layout, where the cover and lines show; stacked, the three cards are simply there.
 */
const COVER_AT = 0;
const lineAt = (i: number) => 450 + i * 650;
const cardAt = (i: number) => lineAt(i) + 380;

/** Sets `armed` (hidden, waiting) on wide screens, then `shown` once most of the tree is on screen. */
function useUsesEntrance() {
  const tree = useRef<HTMLDivElement>(null);
  const [armed, setArmed] = useState(false);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = tree.current;
    if (!el || reducedMotion() || !window.matchMedia('(min-width: 1081px)').matches) return;
    setArmed(true);
    const view = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShown(true);
        view.disconnect();
      },
      { threshold: 0.35 },
    );
    view.observe(el);
    return () => view.disconnect();
  }, []);
  return { tree, armed, shown };
}

/** The cover up top, a dashed line down to each place it gets shown. */
function UseCases() {
  const { interview, portfolio, team } = useCases;
  const { tree, armed, shown } = useUsesEntrance();
  const maskId = useId().replace(/:/g, '');
  return (
    <section className={styles.uses}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{useCases.title}</h2>
        <p className={styles.sectionLede}>{useCases.lede}</p>
      </div>
      <div ref={tree} className={styles.usesTree} data-armed={armed || undefined} data-shown={shown || undefined}>
        <img
          className={styles.usesCover}
          src={useCases.cover.art}
          alt={useCases.cover.title}
          width={340}
          height={192}
          style={delay(COVER_AT)}
        />
        {/*
         * Stretched to the row's width; the strokes keep their weight and dashes. Each line draws by its own
         * mask, a solid stroke that grows along the same path.
         */}
        <svg className={styles.usesLines} viewBox="0 0 1200 96" preserveAspectRatio="none" aria-hidden>
          <defs>
            {USE_LINES.map((d, i) => (
              <mask key={d} id={`${maskId}-${i}`} maskUnits="userSpaceOnUse" x="-20" y="-20" width="1240" height="136">
                <path className={styles.lineReveal} d={d} pathLength={1} style={delay(lineAt(i))} />
              </mask>
            ))}
          </defs>
          {USE_LINES.map((d, i) => (
            <path key={d} d={d} mask={`url(#${maskId}-${i})`} />
          ))}
        </svg>
        <ul className={styles.usesRow}>
          <li className={styles.use} style={delay(cardAt(0))}>
            <div className={styles.useTile} aria-hidden>
              <div className={styles.call}>
                <div className={styles.callBar}>
                  <i />
                  <i />
                  <i />
                </div>
                <div className={styles.callBody}>
                  <div className={styles.slide}>
                    <span className={styles.slideEyebrow}>{interview.eyebrow}</span>
                    <span className={styles.slideFigure}>
                      <span className={styles.slideValue}>{interview.value}</span>
                      <span className={styles.slideCaption}>{interview.caption}</span>
                    </span>
                  </div>
                  <div className={styles.callFaces}>
                    <span className={styles.callFace}>
                      <span className={`${styles.avatar} ${styles.blue}`}>JD</span>
                    </span>
                    <span className={styles.callFace}>
                      <span className={`${styles.avatar} ${styles.pink}`}>AM</span>
                    </span>
                  </div>
                </div>
                <div className={styles.callControls}>
                  <i />
                  <i />
                  <span className={styles.presenting}>{interview.presenting}</span>
                  <i />
                </div>
              </div>
            </div>
            <h3 className={styles.useLabel}>{interview.label}</h3>
          </li>
          <li className={styles.use} style={delay(cardAt(1))}>
            <div className={styles.useTile} aria-hidden>
              <div className={styles.site}>
                <div className={styles.siteBar}>
                  <i />
                  <i />
                  <i />
                  <span className={styles.siteUrl}>{portfolio.site}</span>
                </div>
                <div className={styles.siteBody}>
                  <div className={styles.siteHead}>
                    <span className={styles.siteHeading}>{portfolio.heading}</span>
                    <span className={styles.line} style={{ width: 120 }} />
                  </div>
                  <div className={styles.siteGrid}>
                    <img className={styles.siteCover} src={useCases.cover.art} alt="" />
                    <span className={styles.siteSlots}>
                      <i />
                      <i />
                    </span>
                  </div>
                </div>
                <CursorArrow className={styles.sitePointer} size={22} />
              </div>
            </div>
            <h3 className={styles.useLabel}>{portfolio.label}</h3>
          </li>
          <li className={styles.use} style={delay(cardAt(2))}>
            <div className={styles.useTile} aria-hidden>
              <div className={styles.review}>
                <div className={styles.pdf}>
                  <div className={styles.pdfHead}>
                    <span>PDF</span>
                    <span className={styles.pdfPage}>{team.page}</span>
                  </div>
                  <span className={styles.pdfClaim}>{team.claim}</span>
                  <span className={styles.pdfShot} />
                  <span className={styles.lines}>
                    <span className={styles.line} />
                    <span className={styles.line} style={{ width: '88%' }} />
                    <span className={styles.line} style={{ width: '70%' }} />
                  </span>
                </div>
                <div className={styles.comments}>
                  <span className={styles.comment}>
                    <span className={`${styles.avatar} ${styles.blue} ${styles.avatarSmall}`}>JD</span>
                    <span className={styles.lines}>
                      <span className={styles.line} />
                      <span className={styles.line} style={{ width: '70%' }} />
                    </span>
                  </span>
                  <span className={`${styles.comment} ${styles.commentReply}`}>
                    <span className={`${styles.avatar} ${styles.pink} ${styles.avatarSmall}`}>AM</span>
                    <span className={styles.lines}>
                      <span className={styles.line} />
                      <span className={styles.line} style={{ width: '54%' }} />
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <h3 className={styles.useLabel}>{team.label}</h3>
          </li>
        </ul>
      </div>
    </section>
  );
}

function Privacy() {
  return (
    <section className={styles.privacy}>
      <div className={styles.vault}>
        <div className={styles.vaultHead}>
          <div className={styles.vaultTitleGroup}>
            <Lock />
            <h2 className={styles.vaultTitle}>
              {privacy.title.map((line) => (
                <span key={line} className={styles.headlineLine}>
                  {line}
                </span>
              ))}
            </h2>
          </div>
          <p className={styles.vaultLede}>{privacy.lede}</p>
        </div>
        <ul className={styles.vaultPoints}>
          {privacy.points.map((point) => (
            <li key={point.title} className={styles.vaultPoint}>
              <h3 className={styles.vaultPointTitle}>{point.title}</h3>
              <p className={styles.vaultPointBody}>{point.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * One answer open at a time; the first starts open, as in Paper. A click anywhere on an item toggles it, the
 * open answer included: the question's button stretches its hit area over the whole item.
 */
function Faq() {
  return (
    <section id={FAQ_ID} className={styles.faq}>
      <h2 className={`${styles.sectionTitle} ${styles.faqTitle}`}>{faq.title}</h2>
      <ul className={styles.faqGrid}>
        {faq.items.map((item) => (
          <li key={item.q} className={styles.faqItem}>
            <h3 className={styles.faqQuestion}>{item.q}</h3>
            <p className={styles.faqAnswer}>{item.a}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Footer() {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerBrand}>
        <a className={styles.footerMark} href="#top" aria-label="Deck, back to top" onClick={toSection}>
          <Mark size={30} />
        </a>
        <p className={styles.footerTagline}>{footer.tagline}</p>
      </div>
      <div className={styles.footerBottom}>
        <p className={styles.copyright}>{footer.copyright}</p>
        <ul className={styles.social}>
          {footer.social.map((item) => (
            <li key={item.id}>
              {item.href ? (
                <a className={styles.socialLink} href={item.href} aria-label={item.label} target="_blank" rel="noreferrer">
                  <SocialIcon id={item.id} />
                </a>
              ) : (
                <span className={styles.socialLink} title={item.label}>
                  <SocialIcon id={item.id} />
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </footer>
  );
}

/** The networks' own marks, drawn on a 24 grid: LinkedIn and X as they publish them, Instagram's camera glyph. */
function SocialIcon({ id }: { id: (typeof footer.social)[number]['id'] }) {
  switch (id) {
    case 'linkedin':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="currentColor"
            d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
          />
        </svg>
      );
    case 'x':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="currentColor"
            d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
          />
        </svg>
      );
    case 'instagram':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="2" y="2" width="20" height="20" rx="6" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="12" cy="12" r="4.6" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="17.6" cy="6.4" r="1.4" fill="currentColor" />
        </svg>
      );
  }
}

function Lock() {
  return (
    <svg className={styles.lock} width="40" height="40" viewBox="0 0 24 24" aria-hidden>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill="currentColor"
        d="M12 1C15.038 1 17.5 3.462 17.5 6.5V8.267C19.198 8.754 20.482 10.231 20.724 12.021C20.872 13.125 21 14.295 21 15.5C21 16.705 20.872 17.875 20.724 18.979C20.436 21.111 18.669 22.799 16.486 22.899C15.054 22.965 13.6 23 12 23C10.4 23 8.946 22.965 7.514 22.899C5.331 22.799 3.564 21.111 3.276 18.979C3.128 17.875 3 16.705 3 15.5C3 14.295 3.128 13.125 3.276 12.021C3.518 10.231 4.802 8.754 6.5 8.267V6.5C6.5 3.462 8.962 1 12 1ZM12 10C10.43 10 9.006 10.033 7.605 10.098C6.421 10.152 5.421 11.081 5.259 12.289C5.114 13.365 5 14.43 5 15.5C5 16.57 5.114 17.635 5.259 18.711C5.421 19.919 6.421 20.848 7.605 20.902C9.006 20.967 10.43 21 12 21C13.57 21 14.994 20.967 16.395 20.902C17.579 20.848 18.579 19.919 18.741 18.711C18.886 17.635 19 16.57 19 15.5C19 14.43 18.886 13.365 18.741 12.289C18.579 11.081 17.579 10.152 16.395 10.098C14.994 10.033 13.57 10 12 10ZM12 3C10.067 3 8.5 4.567 8.5 6.5V8.06C9.618 8.02 10.766 8 12 8C13.234 8 14.382 8.02 15.5 8.06V6.5C15.5 4.567 13.933 3 12 3Z"
      />
      <path
        fill="currentColor"
        d="M12.005 14.5C12.557 14.5 13.005 14.948 13.005 15.5C13.005 16.052 12.557 16.5 12.005 16.5H11.996C11.444 16.5 10.996 16.052 10.996 15.5C10.996 14.948 11.444 14.5 11.996 14.5H12.005Z"
      />
    </svg>
  );
}

function Arrow() {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" aria-hidden>
      <path d="M1 7H14M9 2L14 7L9 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
